import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { useKimAddress } from "../../../../../lib/use-kim-address";
import {
  useCardReaderSession,
  type CardReaderReadContext,
} from "../../use-card-reader-session";
import {
  CARD_DATA_UNAVAILABLE_TAG,
  cardReadFailureTag,
} from "../../card-read-failure";
import { useCheckInSend } from "../context";
import { useKioskPolicy } from "../../../../../lib/kiosk-policy/kiosk-policy-context";
import { docCirrusRpcClient } from "../../../../../lib/rpc/doc-cirrus.client";
import { toCheckInCardData } from "../card-data";

const CARD_AUTO_READ_DELAY_MS = 0;

type CardReadResult = Awaited<ReturnType<CardReaderReadContext["readCard"]>>;
type CheckInInsurance = "GKV" | "PKV" | "SELFPAY";

type CardReadOutcome =
  | { status: "ok"; result: CardReadResult }
  | { status: "unreadable"; error: unknown }
  | { status: "error"; error: unknown };

function mapCardInsuranceToCheckInInsurance(
  insuranceType: CardReadResult["cardData"]["insurance"]["insuranceType"],
): CheckInInsurance | undefined {
  switch (insuranceType) {
    case "private":
      return "PKV";
    case "self-pay":
      return "SELFPAY";
    case "statutory":
      return "GKV";
    default:
      return undefined;
  }
}

/**
 * KIM account data rendered by the card-check PKV alternative panel.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const kimInfo: CardCheckKimInfo = null;
 * ```
 */
export type CardCheckKimInfo = ReturnType<typeof useKimAddress>["kimInfo"];

async function readCardOnce(
  readCard: CardReaderReadContext["readCard"],
): Promise<CardReadOutcome> {
  try {
    return {
      status: "ok",
      result: await readCard(),
    };
  } catch (error) {
    if (cardReadFailureTag(error) === CARD_DATA_UNAVAILABLE_TAG) {
      return { status: "unreadable", error };
    }

    // A transport failure does not prove that the physical read stopped.
    // Another read must be an explicit patient action, never an automatic retry.
    return { status: "error", error };
  }
}

/**
 * Coordinates card-reader polling, explicit retries, and check-in machine events.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * function ExampleCardCheckControls() {
 *   const controller = useCardCheckController();
 *   return <button onClick={controller.handleRetry}>Retry</button>;
 * }
 * ```
 */
export function useCardCheckController() {
  const { t } = useTranslation();
  const send = useCheckInSend();
  const kioskPolicy = useKioskPolicy();
  const creationAllowed =
    kioskPolicy.source === "current" &&
    kioskPolicy.patientCreationEnabled;
  const [showPkvCheckin, setShowPkvCheckin] = useState(false);
  const manualFallbackRequestedRef = useRef(false);
  const { kimInfo } = useKimAddress();

  const handleReadCard = useCallback(
    async ({
      readCard,
      setErrorMessage,
      setPhase,
    }: CardReaderReadContext) => {
      if (manualFallbackRequestedRef.current) return;

      const outcome = await readCardOnce(readCard);

      if (manualFallbackRequestedRef.current) {
        if (outcome.status === "ok") {
          await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: outcome.result.crlogId });
        }
        return;
      }

      if (outcome.status === "unreadable") {
        frontdeskDiagnosticLogger.warn("Inserted card returned no patient data", {
          outcome: "card-unreadable",
        });
        setErrorMessage(t("self-service.check-in.card-check.unreadable-help"));
        setPhase("card-unreadable");
        return;
      }

      if (outcome.status === "error") {
        frontdeskDiagnosticLogger.error("Failed to read card", {
          error: outcome.error,
        });
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("error");
        return;
      }

      const { result } = outcome;
      if ((result.status === "APPLIED" || result.status === "MERGED") && !result.patientId) {
        await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("error");
        return;
      }
      const insurance = mapCardInsuranceToCheckInInsurance(
        result.cardData.insurance.insuranceType,
      );
      if (!insurance) {
        await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
        const error = new Error(
          "Card response contained an unsupported insurance type",
        );
        void reportOperationalError({
          action: "card-reader.insurance-unrecognized",
          severity: "error",
          source: "invariant",
          component: "useCardCheckController",
          error,
          context: {
            insuranceRecognized: false,
          },
        });
        setErrorMessage(t("self-service.check-in.card-check.read-error"));
        setPhase("error");
        return;
      }

      switch (result.status) {
        case "APPLIED":
          send({
            type: "CARD_OK",
            hasDiff: false,
            patientId: result.patientId,
            insurance,
            egkAttestation: result.egkAttestation,
          });
          return;

        case "MERGED":
          if (!result.possibleActions.includes("apply") || !result.diff) {
            await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
            setErrorMessage(t("self-service.check-in.card-check.read-error"));
            setPhase("error");
            return;
          }
          send({
            type: "CARD_OK",
            hasDiff: true,
            diffData: result.diff ? [...result.diff] : undefined,
            patientId: result.patientId,
            insurance,
            crlogId: result.crlogId,
            egkAttestation: result.egkAttestation,
          });
          return;

        case "MATCHING":
          if (result.matchedPatients && result.matchedPatients.length > 0) {
            if (!result.possibleActions.includes("matching_select")) {
              await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
              setErrorMessage(t("self-service.check-in.card-check.read-error"));
              setPhase("error");
              return;
            }
            send({
              type: "MULTIPLE_PATIENTS_FOUND",
              insurance,
              matchedPatients: result.matchedPatients.map((patient) => ({
                id: patient.id,
                givenName: patient.givenName,
              })),
              crlogId: result.crlogId,
              egkAttestation: result.egkAttestation,
            });
            return;
          }

          if (creationAllowed) {
            send({
              type: "REGISTER_FROM_CARD",
              cardData: toCheckInCardData(result.cardData),
              crlogId: result.crlogId,
              egkAttestation: result.egkAttestation,
            });
          } else {
            await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
            send({ type: "PATIENT_CREATION_RESTRICTED" });
          }
          return;

        case "CANCELLED":
          setErrorMessage(t("self-service.check-in.card-check.read-error"));
          setPhase("error");
          return;

        default:
          void reportOperationalError({
            action: "card-reader.status-unrecognized",
            severity: "error",
            source: "invariant",
            component: "useCardCheckController",
            error: new Error("Card response contained an unsupported status"),
            context: {
              cardStatusRecognized: false,
            },
          });
          setErrorMessage(t("self-service.check-in.card-check.read-error"));
          setPhase("error");
          return;
      }
    },
    [creationAllowed, send, t],
  );

  const handleReadCardError = useCallback(
    (error: unknown, { terminalId }: { terminalId: string | null }) => {
      frontdeskDiagnosticLogger.error("Failed to read card", {
        error,
        hasTerminalId: Boolean(terminalId),
      });
    },
    [],
  );

  const handlePollError = useCallback(
    (error: unknown, { terminalId }: { terminalId: string | null }) => {
      frontdeskDiagnosticLogger.error("Failed to check for cards", {
        error,
        hasTerminalId: Boolean(terminalId),
      });
    },
    [],
  );

  const cardReader = useCardReaderSession({
    enabled: true,
    settingsErrorMessage: t("self-service.check-in.card-check.settings-error"),
    unreadableCardMessage: t("self-service.check-in.card-check.unreadable-help"),
    readErrorMessage: t("self-service.check-in.card-check.read-error"),
    autoReadDelayMs: CARD_AUTO_READ_DELAY_MS,
    onReadCard: handleReadCard,
    onReadError: handleReadCardError,
    onPollError: handlePollError,
  });

  const { phase: cardState } = cardReader;

  const handleNoCard = useCallback(() => {
    manualFallbackRequestedRef.current = true;
    send({ type: "CARD_MISSING" });
  }, [send]);

  const handleRetry = useCallback(() => {
    manualFallbackRequestedRef.current = false;
    cardReader.retry();
  }, [cardReader]);

  const handleManualFallbackPointerDown = useCallback(() => {
    manualFallbackRequestedRef.current = true;
  }, []);

  const handleTogglePkvCheckin = useCallback(() => {
    setShowPkvCheckin((current) => !current);
  }, []);

  return {
    cardState,
    errorMessage: cardReader.errorMessage ?? undefined,
    detectedCards: cardReader.detectedCards,
    connectionWarning: cardReader.connectionWarning,
    showPkvCheckin,
    kimInfo,
    handleReadCard: cardReader.readCard,
    handleRetry,
    handleNoCard,
    handleManualFallbackPointerDown,
    handleTogglePkvCheckin,
  };
}
